import {
  keepPreviousData,
  queryOptions,
  useMutation,
} from "@tanstack/react-query";
import { type AxiosResponse } from "axios";

import { api } from "@/api/api";
import { queryClient } from "@/app/queryClient";
import type {
  Traveller,
  TravellerRemovedEventData,
  TravellerUpdatedEventData,
} from "@/features/travellers/types/types";
import type {
  CreateTravelerBodyInputDto,
  FindUserTravelersOutputDto,
  SingleTravelerOutputDto,
  UpdateTravelerBodyInputDto,
} from "@/types/codegen";

export const travellersQueryOptions = () =>
  queryOptions({
    queryKey: ["travelers"],
    queryFn: ({ signal }) =>
      api.get<FindUserTravelersOutputDto>("/travelers", {
        signal,
        params: {
          page: 1,
          perPage: 20,
        },
      }),
    placeholderData: keepPreviousData,
    select: (response) => response.data.data.travelers,
  });

export const useDeleteTravellerMutation = () =>
  useMutation({
    mutationFn: (travellerId: string) =>
      api.delete(`/travelers/${travellerId}`),
    onSuccess: (_, travellerId) => {
      queryClient.invalidateQueries({
        queryKey: travellersQueryOptions().queryKey,
      });

      const event = new CustomEvent<TravellerRemovedEventData>(
        "travellerremoved",
        {
          detail: { travellerId },
        },
      );

      window.dispatchEvent(event);
    },
  });

type UseUpdateTravellerMutationParams = {
  onSuccess?: (newTraveller: Traveller) => void;
};

export const useUpdateTravellerMutation = ({
  onSuccess,
}: UseUpdateTravellerMutationParams = {}) =>
  useMutation({
    mutationFn: ({
      travellerId,
      data,
    }: {
      travellerId: string;
      data: UpdateTravelerBodyInputDto;
    }) => api.put<SingleTravelerOutputDto>(`/travelers/${travellerId}`, data),
    onMutate: ({ travellerId }) => {
      const cache = queryClient
        .getQueryCache()
        .find<AxiosResponse<FindUserTravelersOutputDto>>({
          queryKey: travellersQueryOptions().queryKey,
        });

      const travellerOldData = cache?.state.data?.data.data.travelers.find(
        (traveller) => traveller.id === travellerId,
      );

      return travellerOldData;
    },
    onSuccess: async (response, variables, travellerOldData) => {
      const event = new CustomEvent<TravellerUpdatedEventData>(
        "travellerupdated",
        {
          detail: {
            travellerOldData,
            travellerNewData: response.data.data,
          },
        },
      );

      window.dispatchEvent(event);

      await queryClient.refetchQueries({
        queryKey: travellersQueryOptions().queryKey,
      });

      // to ensure that the new traveller is in the cache when the event is handled
      setTimeout(() => {
        onSuccess?.(response.data.data);
      }, 0);
    },
  });

type UseNewTravellerMutationParams = {
  onSuccess?: (newTraveller: Traveller) => void;
};

export const useNewTravellerMutation = ({
  onSuccess,
}: UseNewTravellerMutationParams = {}) =>
  useMutation({
    mutationFn: (body: CreateTravelerBodyInputDto) =>
      api.post<SingleTravelerOutputDto>("/travelers", body),
    onSuccess: async (response) => {
      await queryClient.refetchQueries({
        queryKey: travellersQueryOptions().queryKey,
      });

      // to ensure that the new traveller is in the cache when the event is handled
      setTimeout(() => {
        onSuccess?.(response.data.data);
      }, 0);
    },
  });
